import React from 'react';
import './VolumeBottomSheet.css';

/**
 * Props for VolumeBottomSheet component
 * 
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5, 14.6
 */
export interface VolumeBottomSheetProps {
  /** Track index for the selected track */
  trackIndex: number;
  /** Current volume value (0.0 - 1.0) */
  volume: number;
  /** Track name for display */
  trackName: string;
  /** Callback when volume changes */
  onVolumeChange: (volume: number) => void;
  /** Callback when sheet is closed */
  onClose: () => void;
}

/**
 * VolumeBottomSheet Component
 * 
 * Bottom sheet for adjusting track volume.
 * Slides up from bottom with z-index: 99.
 * Shows volume slider (horizontal) and current volume value.
 * Updates volume in real-time as slider is adjusted.
 * Provides close button.
 * Saves volume when sheet is closed.
 * 
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5, 14.6
 */
export const VolumeBottomSheet: React.FC<VolumeBottomSheetProps> = ({
  trackIndex: _trackIndex,
  volume,
  trackName,
  onVolumeChange,
  onClose,
}) => {
  /**
   * Handle volume slider change
   * Requirement 14.4: Update volume in real-time as slider is adjusted
   */
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    onVolumeChange(newVolume);
  };

  /**
   * Format volume as percentage for display
   * Requirement 14.3: Display current volume value
   */
  const volumePercentage = Math.round(volume * 100);

  /**
   * Handle backdrop click to close sheet
   */
  const handleBackdropClick = () => {
    onClose();
  };

  /**
   * Prevent sheet content clicks from closing the sheet
   */
  const handleSheetClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      {/* Backdrop overlay - Requirement 14.1: Bottom sheet sliding up from bottom */}
      <div 
        className="volume-bottom-sheet-backdrop" 
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      
      {/* Sheet container - Requirement 14.1: z-index: 99 */}
      <div 
        className="volume-bottom-sheet" 
        onClick={handleSheetClick}
        role="dialog" 
        aria-label="Volume control"
        aria-modal="true"
      >
        {/* Header with close button - Requirement 14.5 */}
        <div className="volume-bottom-sheet-header">
          <div className="volume-bottom-sheet-title-section">
            <h3 className="volume-bottom-sheet-title">Volume</h3>
            <p className="volume-bottom-sheet-subtitle">
              {trackName}
            </p>
          </div>
          <button
            className="volume-bottom-sheet-close"
            onClick={onClose}
            aria-label="Close volume control"
            title="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Volume control content - Requirements 14.2, 14.3, 14.4 */}
        <div className="volume-bottom-sheet-content">
          {/* Volume icon and percentage display - Requirement 14.3 */}
          <div className="volume-display">
            <div className="volume-icon">
              <i className={`fa-solid ${
                volume === 0 ? 'fa-volume-xmark' :
                volume < 0.5 ? 'fa-volume-low' :
                'fa-volume-high'
              }`}></i>
            </div>
            <div className="volume-percentage">
              {volumePercentage}%
            </div>
          </div>

          {/* Volume slider - Requirement 14.2, 14.4 */}
          <div className="volume-slider-container">
            <span className="volume-slider-label" aria-hidden="true">0%</span>
            <input
              type="range"
              className="volume-slider"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              aria-label={`Volume for ${trackName}`}
              aria-valuemin={0}
              aria-valuemax={1}
              aria-valuenow={volume}
              aria-valuetext={`${volumePercentage}%`}
              title={`Volume: ${volumePercentage}%`}
            />
            <span className="volume-slider-label" aria-hidden="true">100%</span>
          </div>
        </div>
      </div>
    </>
  );
};
